import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { ehLicencaVencida } from "../../errors/licencaRules";
import { socketConnection } from "../../services/socket";
import { situacaoDoVencimento, avisoDoLogin } from "./loginRules";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  
  // Ref para manter referência dos listeners ativos
  const listenersRef = useRef(new Set());

  // Interceptors do API (mantém como estava)
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (ehLicencaVencida(error)) {
        if (window.location.pathname !== "/financeiro-aberto") {
          history.push("/financeiro-aberto");
        }
        return Promise.reject(error);
      }
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        const { data } = await api.post("/auth/refresh_token");
        if (data) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(originalRequest);
      }
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
      }
      return Promise.reject(error);
    }
  );

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
        } catch (err) {
          toastError(err);
        }
      }
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      console.log("Configurando socket para user", user.id, "company", user.companyId);
      
      // Limpar listeners anteriores
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }

      // Criar nova conexão socket
      const socketInstance = socketConnection({ user: {
        companyId: user.companyId,
        id: user.id }
      });
      
      if (socketInstance) {
        setSocket(socketInstance);

        // Aguardar um pouco para garantir que o socket está configurado
        setTimeout(() => {
          const eventName = `company-${user.companyId}-user`;
          
          const handleUserUpdate = (data) => {
            if (data.action === "update" && data.user.id === user.id) {
              setUser(data.user);
            }
          };

          // Verificar se o socket tem o método 'on'
          if (socketInstance && typeof socketInstance.on === 'function') {
            socketInstance.on(eventName, handleUserUpdate);
            listenersRef.current.add(eventName);
            console.log(`Listener adicionado para: ${eventName}`);

            const forceLogoutEvent = `company-${user.companyId}-forceLogout`;
            const handleForceLogout = (data) => {
              const mySessionId = localStorage.getItem("sessionId");
              if (
                data.userId === user.id &&
                data.sessionId &&
                data.sessionId !== mySessionId
              ) {
                localStorage.removeItem("token");
                localStorage.removeItem("sessionId");
                api.defaults.headers.Authorization = undefined;
                setIsAuth(false);
                setUser({});
                toast.error(
                  "Sua conta foi acessada em outro dispositivo. Você foi desconectado."
                );
                history.push("/login");
              }
            };
            socketInstance.on(forceLogoutEvent, handleForceLogout);
            listenersRef.current.add(forceLogoutEvent);

            // Pagamento de assinatura confirmado: a `company` do evento traz o
            // dueDate novo (e o plano, no upgrade) sem precisar de reload.
            const paymentEvent = `company-${user.companyId}-payment`;
            const handlePayment = (data) => {
              if (data?.action === "CONCLUIDA" && data.company) {
                setUser((prev) => ({
                  ...prev,
                  company: { ...(prev.company || {}), ...data.company },
                }));
              }
            };
            socketInstance.on(paymentEvent, handlePayment);
            listenersRef.current.add(paymentEvent);

            const subscriptionEvent = `company-${user.companyId}-subscription`;
            const handleSubscription = (data) => {
              if (data?.action === "RENEWAL_FAILED") {
                toast.warn(i18n.t("subscription.card.renewalFailed"));
              }
            };
            socketInstance.on(subscriptionEvent, handleSubscription);
            listenersRef.current.add(subscriptionEvent);
          } else {
            console.error("Socket instance não tem método 'on'", socketInstance);
          }
        }, 100);
      }
    }

    // Cleanup function
    return () => {
      if (socket && listenersRef.current.size > 0) {
        console.log("Limpando listeners do socket para user", user.id);
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
      }
    };
  }, [user.id, user.companyId]); // Dependências específicas

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const completeLogin = (data) => {
    const {
      user: { company },
    } = data;

    // Lógica de configurações da empresa (mantém como estava)
    if (
      has(company, "companieSettings") &&
      isArray(company.companieSettings[0])
    ) {
      const setting = company.companieSettings[0].find(
        (s) => s.key === "campaignsEnabled"
      );
      if (setting && setting.value === "true") {
        localStorage.setItem("cshow", null);
      }
    }

    if (
      has(company, "companieSettings") &&
      isArray(company.companieSettings[0])
    ) {
      const setting = company.companieSettings[0].find(
        (s) => s.key === "sendSignMessage"
      );

      const signEnable = setting.value === "enable";

      if (setting && setting.value === "enabled") {
        localStorage.setItem("sendSignMessage", signEnable);
      }
    }

    localStorage.setItem("profileImage", data.user.profileImage);

    // Licença vencida também entra autenticada (token salvo, usuário no
    // contexto): a única diferença é o destino. Antes o token não era salvo
    // nesse ramo e um recarregamento devolvia o admin ao login, sem chegar ao
    // pagamento. O Route.js mantém quem está vencido preso a /financeiro-aberto.
    const situacao = situacaoDoVencimento(data.user.company);

    localStorage.setItem("token", JSON.stringify(data.token));
    if (data.sessionId) {
      localStorage.setItem("sessionId", data.sessionId);
    }
    localStorage.setItem(
      "companyDueDate",
      situacao.vencimento || "31/12/2999"
    );
    api.defaults.headers.Authorization = `Bearer ${data.token}`;
    setUser(data.user || data);
    setIsAuth(true);

    const aviso = avisoDoLogin(situacao);
    if (situacao.expirado) {
      toastError(aviso.texto);
      history.push("/financeiro-aberto");
    } else {
      toast.success(i18n.t("auth.toasts.success"));
      if (aviso) toast.warn(aviso.texto);
      history.push("/tickets");
    }
    setLoading(false);
  };

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);

      // 2FA required - return signal to component
      if (data.requiresTwoFactor) {
        setLoading(false);
        return {
          requiresTwoFactor: true,
          twoFactorSessionToken: data.twoFactorSessionToken
        };
      }

      // 2FA setup required (mandatory policy) - don't login, return signal
      if (data.requiresTwoFactorSetup) {
        setLoading(false);
        return {
          requiresTwoFactorSetup: true,
          twoFactorSessionToken: data.twoFactorSessionToken
        };
      }

      // New device requires confirmation - return signal to component
      if (data.requiresDeviceConfirmation) {
        setLoading(false);
        return {
          requiresDeviceConfirmation: true,
          deviceConfirmSessionToken: data.deviceConfirmSessionToken
        };
      }

      completeLogin(data);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleValidate2FA = async (twoFactorSessionToken, totpCode) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/validate-2fa", {
        twoFactorSessionToken,
        totpCode
      });
      completeLogin(data);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleConfirmDevice = async (deviceConfirmSessionToken, otp) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/confirm-device", {
        deviceConfirmSessionToken,
        otp
      });
      completeLogin(data);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleSetupMandatory2FA = async (twoFactorSessionToken) => {
    try {
      const { data } = await api.post("/auth/login/setup-2fa", {
        twoFactorSessionToken
      });
      return data; // { otpauthUri, secret }
    } catch (err) {
      toastError(err);
      throw err;
    }
  };

  const handleVerifyMandatory2FA = async (twoFactorSessionToken, token) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login/verify-2fa-setup", {
        twoFactorSessionToken,
        token
      });
      // data contains { token, user, recoveryCodes }
      // Complete the login
      completeLogin(data);
      return data.recoveryCodes;
    } catch (err) {
      toastError(err);
      setLoading(false);
      throw err;
    }
  };

  const handleForgotPassword = async (email) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/forgot", { email });
      return data.passwordResetSessionToken;
    } catch (err) {
      toastError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async (passwordResetSessionToken, otp) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/password/verify-code", {
        passwordResetSessionToken,
        otp
      });
      return data; // { passwordResetConfirmToken, requiresTwoFactor }
    } catch (err) {
      toastError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Não cria sessão de propósito: o usuário volta ao login e entra digitando,
  // para o login normal reaplicar sozinho as regras de dispositivo e de 2FA.
  const handleResetPassword = async (
    passwordResetConfirmToken,
    password,
    totpCode
  ) => {
    setLoading(true);
    try {
      await api.post("/auth/password/reset", {
        passwordResetConfirmToken,
        password,
        totpCode
      });
    } catch (err) {
      toastError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      // Limpar socket antes do logout
      if (socket) {
        listenersRef.current.forEach(eventName => {
          if (socket.off) {
            socket.off(eventName);
          }
        });
        listenersRef.current.clear();
        
        if (typeof socket.disconnect === 'function') {
          socket.disconnect();
        }
      }

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    handleValidate2FA,
    handleConfirmDevice,
    handleSetupMandatory2FA,
    handleVerifyMandatory2FA,
    handleForgotPassword,
    handleVerifyResetCode,
    handleResetPassword,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;